<script setup lang="ts">
const activeTabId = ref('');
const slots = useSlots();

interface ITabsProps {
  defaultTab?: string;
  headerContainerClass?: string;
  headerInnerClass?: string;
  tabsContentClass?: string;
  tabButtonClasses?: string;
  alternateBgColors?: boolean;
  stretch?: boolean;
}

const props = withDefaults(defineProps<ITabsProps>(), {
  headerContainerClass: '',
  activeTabClass: '',
  tabsContentClass: '',
  tabButtonClasses: '',
  stretch: false,
  alternateBgColors: false,
});

const emits = defineEmits(['tabChange']);

const tabs = computed(() => {
  //Accounting for v-for or v-if fragmenting useSlots helper
  const tablist: any =
    !!slots?.tabList()[0]?.props?.id ||
    !Array.isArray(slots?.tabList()[0]?.children)
      ? slots?.tabList()
      : slots?.tabList()[0]?.children;

  if (tablist?.length > 0) {
    return tablist?.flatMap((vnode) => {
      if (!!vnode?.props)
        return {
          title: vnode?.props?.title,
          id: vnode?.props?.id,
          icon: vnode?.props?.icon,
        };
      return [];
    });
  } else return [];
});

watchEffect(() => {
  if (tabs?.value?.length > 0) {
    activeTabId.value = props?.defaultTab ?? tabs?.value[0]?.id;
  }
});

const selectTab = (id: string) => {
  activeTabId.value = id;
  emits('tabChange', activeTabId.value);
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
    inline: 'center',
    block: 'nearest',
  });
};

// Provide the activeTabId to child Tab components
provide('activeTabId', activeTabId);
</script>

<template>
  <div>
    <slot name="header" />
    <div
      v-if="tabs.length > 1"
      :class="[headerContainerClass, 'flex-center w-full']"
    >
      <div
        :class="[
          alternateBgColors ? 'dark:bg-dark-900 bg-light-400' : 'bg-layer',
          { 'gap-2 w-full mx-2': stretch },
          headerInnerClass,
          'tabs-header',
        ]"
      >
        <div
          v-for="tab in tabs"
          :id="tab.id"
          :key="tab.id"
          :class="[
            activeTabId === tab.id
              ? alternateBgColors
                ? 'dark:bg-dark-700 bg-light-50'
                : 'bg-primary'
              : '',
            { 'w-full': stretch },
            'tabs-list relative',
            tabButtonClasses,
          ]"
          @click="selectTab(tab.id)"
        >
          <component
            :is="tab.icon"
            v-if="tab.icon"
            class="w-4 h-4 shrink-0"
          />
          <span
            class="tabs-heading text-base-priority text-sm truncate"
            v-html="tab.title"
          />
        </div>
      </div>
    </div>
    <slot name="divider" />
    <div
      class="tabs-content"
      v-bind="{ ...(!!tabsContentClass && { class: tabsContentClass }) }"
    >
      <slot name="tabList" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.tabs-header {
  @apply flex p-1 rounded-2xl overflow-x-auto scrollbar-none;
}

.tabs-list {
  @apply flex items-center gap-2 text-nowrap rounded-xl p-2 font-bold cursor-pointer;
}

.tabs-heading {
  @apply capitalize font-bold w-full text-center;
}
</style>
